// src/components/VisitNotification.tsx
import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Button,
  Chip,
  Box,
  Typography,
  Slide,
} from '@mui/material';
import {
  LocationOn,
  PhotoCamera,
  CheckCircle,
  Visibility,
} from '@mui/icons-material';
import { LandmarkVerification } from '../types/visits';
import { useT } from '../i18n/useT';

interface VisitNotificationProps {
  verification: LandmarkVerification | null;
  visitSaved: boolean;
  visitId?: string;
  onClose: () => void;
  onViewVisits?: () => void;
}

const getLandmarkTypeIcon = (type?: string) => {
  switch (type) {
    case 'monument':
      return '🏛️';
    case 'museum':
      return '🏛️';
    case 'natural':
      return '🏞️';
    case 'religious':
      return '⛪';
    case 'architectural':
      return '🏗️';
    case 'cultural':
      return '🎭';
    case 'historical':
      return '📜';
    default:
      return '📍';
  }
};

const getLandmarkTypeColor = (type?: string) => {
  switch (type) {
    case 'monument':
    case 'historical':
      return 'primary';
    case 'museum':
    case 'cultural':
      return 'secondary';
    case 'natural':
      return 'success';
    case 'religious':
      return 'warning';
    case 'architectural':
      return 'info';
    default:
      return 'default';
  }
};

export const VisitNotification: React.FC<VisitNotificationProps> = ({
  verification,
  visitSaved,
  visitId,
  onClose,
  onViewVisits,
}) => {
  const t = useT();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (verification && visitSaved) {
      setOpen(true);
    }
  }, [verification, visitSaved]);

  const handleClose = () => {
    setOpen(false);
    setTimeout(onClose, 300); // Allow animation to complete
  };

  const handleViewVisits = () => {
    onViewVisits?.();
    handleClose();
  };

  if (!verification || !visitSaved) {
    return null;
  }

  const typeIcon = getLandmarkTypeIcon(verification.landmark_type);
  const typeColor = getLandmarkTypeColor(verification.landmark_type);

  return (
    <Snackbar
      open={open}
      autoHideDuration={8000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'down' } as any}
    >
      <Alert
        severity="success"
        variant="filled"
        onClose={handleClose}
        sx={{
          minWidth: 320,
          maxWidth: 400,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
        icon={<CheckCircle />}
      >
        <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>{typeIcon}</span>
          
          {t("Landmark Visit Recorded!")}
        </AlertTitle>
        
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {verification.landmark_name}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {verification.landmark_type && (
              <Chip
                label={verification.landmark_type.replace('_', ' ')}
                size="small"
                color={typeColor as any}
                variant="outlined"
              />
            )}
            <Chip
              label={`${verification.confidence}% confidence`}
              size="small"
              variant="outlined"
            />
          </Box>

          {verification.reasoning && (
            <Typography variant="caption" sx={{ display: 'block', mb: 2, opacity: 0.9 }}>
              {verification.reasoning}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleClose}
              sx={{ color: 'inherit', borderColor: 'rgba(255,255,255,0.5)' }}
            >
              
              {t("Dismiss")}
            </Button>
            
            {onViewVisits && (
              <Button
                size="small"
                variant="contained"
                onClick={handleViewVisits}
                startIcon={<Visibility />}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                }}
              >
                
                {t("View Visits")}
              </Button>
            )}
          </Box>
        </Box>
      </Alert>
    </Snackbar>
  );
};